import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

/**
 * BulkEditSummaryDialog - Display results of bulk operations (save/delete)
 * 
 * @param {boolean} open - Whether the dialog is open
 * @param {string} operation - Operation type: 'save' or 'delete'
 * @param {Object} results - Results object with structure:
 *   {
 *     successful: number,
 *     failed: [{
 *       id: number,
 *       gl_key: string,
 *       account_name: string,
 *       reason: string,
 *       dependencies: {
 *         drivers: [...],
 *         changes: [...],
 *         formulas: [...]
 *       }
 *     }]
 *   }
 * @param {function} onClose - Callback when dialog is closed
 */
const BulkEditSummaryDialog = ({ open, operation = 'save', results = {}, onClose }) => {
  const { successful = 0, failed = [] } = results;
  const hasFailures = failed.length > 0;
  const operationPastTense = operation === 'save' ? 'saved' : 'deleted';

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>
        Bulk Operation Results
      </DialogTitle>
      
      <DialogContent>
        {/* Success Summary */}
        {successful > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2, bgcolor: '#d4edda', borderRadius: 1 }}>
            <CheckCircleIcon sx={{ color: '#155724', mr: 1 }} />
            <Typography variant="body1" sx={{ color: '#155724' }}>
              Successfully {operationPastTense} {successful} account{successful !== 1 ? 's' : ''}
            </Typography>
          </Box>
        )}

        {/* Failure Summary */}
        {hasFailures && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 2, bgcolor: '#f8d7da', borderRadius: 1 }}>
              <ErrorIcon sx={{ color: '#721c24', mr: 1 }} />
              <Typography variant="body1" sx={{ color: '#721c24' }}>
                Failed to {operation} {failed.length} account{failed.length !== 1 ? 's' : ''}:
              </Typography>
            </Box>

            <List sx={{ mt: 2, maxHeight: '320px', overflowY: 'auto' }}>
              {failed.map((item, index) => (
                <React.Fragment key={index}>
                  <ListItem 
                    sx={{ 
                      flexDirection: 'column', 
                      alignItems: 'flex-start',
                      bgcolor: '#fff3cd',
                      borderRadius: 1,
                      mb: 1,
                      p: 2
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      • {item.gl_key} - {item.account_name || 'Unknown Account'}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary', mb: 1 }}>
                      Reason: {item.reason}
                    </Typography>

                    {/* Show dependencies for delete operations */}
                    {operation === 'delete' && item.dependencies && (
                      <Box sx={{ ml: 2, mt: 1 }}>
                        {item.dependencies.drivers && item.dependencies.drivers.length > 0 && (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Dependencies: Used as driver by {item.dependencies.drivers.map(d => d.gl_key).join(', ')}
                          </Typography>
                        )}
                        
                        {item.dependencies.changes && item.dependencies.changes.length > 0 && (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Dependencies: Referenced in {item.dependencies.changes.length} change{item.dependencies.changes.length !== 1 ? 's' : ''}
                          </Typography>
                        )}
                        
                        {item.dependencies.formulas && item.dependencies.formulas.length > 0 && (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Dependencies: Referenced in {item.dependencies.formulas.length} formula{item.dependencies.formulas.length !== 1 ? 's' : ''}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </ListItem>
                  {index < failed.length - 1 && <Divider sx={{ my: 1 }} />}
                </React.Fragment>
              ))}
            </List>
          </>
        )}

        {/* No results message */}
        {successful === 0 && !hasFailures && (
          <Typography variant="body1" color="text.secondary">
            No accounts were {operationPastTense}.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkEditSummaryDialog;
